// material-ui
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';

// assets
import SearchOutlined from '@ant-design/icons/SearchOutlined';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ==============================|| HEADER CONTENT - SEARCH (find-in-page, robust) ||============================== //

export default function Search() {
  const [q, setQ] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const matchesRef = useRef([]);   // <mark> nodes
  const inputRef = useRef(null);

  // ——— Utils ———
  const debounce = (fn, ms = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const clearHighlights = () => {
    document.querySelectorAll('mark.ui-find').forEach((el) => {
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      parent.normalize?.();
    });
    matchesRef.current = [];
    setCurrentIndex(0);
  };

  // Collect all eligible text nodes first (snapshot) so DOM edits don't break iteration
  const collectTextNodes = () => {
    const nodes = [];
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const p = node.parentElement;
        if (!p) return NodeFilter.FILTER_REJECT;
        const tag = p.tagName;
        if (
          ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME'].includes(tag) ||
          p.isContentEditable ||
          p.closest('input, textarea, [contenteditable="true"], [aria-hidden="true"], [data-no-search="true"]') ||
          p.closest('#header-search') // exclude the search input itself
        ) return NodeFilter.FILTER_REJECT;

        const style = window.getComputedStyle(p);
        if (style.visibility === 'hidden' || style.display === 'none') return NodeFilter.FILTER_REJECT;

        return node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    let n;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  };

  // Highlight all occurrences within a single text node using splitText (case-insensitive)
  const highlightInTextNode = (textNode, needleLower, marksOut) => {
    let node = textNode;
    let textLower = node.nodeValue.toLowerCase();
    let idx = textLower.indexOf(needleLower);

    while (idx !== -1) {
      // split at start of match
      const startNode = node.splitText(idx);
      // split at end of match
      const endNode = startNode.splitText(needleLower.length);

      // wrap the match
      const mark = document.createElement('mark');
      mark.className = 'ui-find';
      mark.textContent = startNode.nodeValue;
      startNode.parentNode.replaceChild(mark, startNode);
      marksOut.push(mark);

      // continue scanning the remainder
      node = endNode;
      textLower = node.nodeValue.toLowerCase();
      idx = textLower.indexOf(needleLower);
    }
  };

  const runHighlight = (term) => {
    clearHighlights();
    const needle = term?.trim();
    if (!needle) return;

    const nodes = collectTextNodes();
    const needleLower = needle.toLowerCase();
    const marks = [];

    nodes.forEach((n) => highlightInTextNode(n, needleLower, marks));

    matchesRef.current = marks;
    if (marks.length) {
      setCurrentIndex(0);
      focusMatch(0);
    }
  };

  const focusMatch = (i) => {
    const list = matchesRef.current;
    if (!list.length) return;
    const idx = ((i % list.length) + list.length) % list.length;
    setCurrentIndex(idx);
    const el = list[idx];
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    list.forEach((m, j) => m.classList.toggle('ui-find-active', j === idx));
  };

  const debouncedHighlight = useMemo(() => debounce(runHighlight, 120), []);

  const handleChange = useCallback(
    (e) => {
      const v = e.target.value;
      setQ(v);
      debouncedHighlight(v);
    },
    [debouncedHighlight]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (!matchesRef.current.length) return;
      e.preventDefault();
      focusMatch(e.shiftKey ? currentIndex - 1 : currentIndex + 1);
    } else if (e.key === 'Escape') {
      setQ('');
      clearHighlights();
      inputRef.current?.blur();
    }
  };

  // Ctrl/⌘ + K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // cleanup
  useEffect(() => () => clearHighlights(), []);

  return (
    <Box
      sx={{
        width: '100%',
        ml: { xs: 0, md: 1 },
        '& mark.ui-find': {
          background: 'rgba(255, 215, 0, 0.6)',
          padding: '0 2px',
          borderRadius: '2px'
        },
        '& mark.ui-find.ui-find-active': {
          outline: '2px solid',
          outlineColor: 'rgba(25,118,210,0.9)'
        }
      }}
    >
      <FormControl sx={{ width: { xs: '100%', md: 260 } }}>
        <OutlinedInput
          inputRef={inputRef}
          size="small"
          id="header-search"
          value={q}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          startAdornment={
            <InputAdornment position="start" sx={{ mr: -0.5 }}>
              <SearchOutlined />
            </InputAdornment>
          }
          aria-describedby="header-search-text"
          placeholder="Search page… (Ctrl/⌘+K, Enter to jump)"
        />
      </FormControl>
    </Box>
  );
}
